'use client';

import React, { useEffect, useRef } from 'react';

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particles: { x: number; y: number; vx: number; vy: number; size: number; opacity: number }[] = [];
    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 1.5 + 0.5,
        opacity: Math.random() * 0.5 + 0.1,
      });
    }

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(96, 165, 250, ${p.opacity})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: 'radial-gradient(80% 60% at 50% -10%, rgba(59, 130, 246, 0.12) 0%, transparent 70%), rgb(5, 5, 5)' }}
    >
      {/* Animated Grid */}
      <div className="absolute inset-0 animated-grid pointer-events-none" />

      {/* Particle Canvas */}
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.55 }}
      />

      {/* Orbs */}
      <div
        className="float-orb pulse-glow-anim absolute pointer-events-none"
        style={{ width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59, 130, 246, 0.18) 0%, transparent 70%)', top: '10%', left: '10%', filter: 'blur(40px)' }}
      />
      <div
        className="float-orb-alt pulse-glow-anim absolute pointer-events-none"
        style={{ width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(157, 76, 221, 0.16) 0%, transparent 70%)', bottom: '15%', right: '10%', filter: 'blur(50px)' }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 pb-20 text-center">
        {/* Eyebrow */}
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-[0.18em] uppercase mb-8"
          style={{ background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.25)', color: 'rgb(96, 165, 250)' }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block"
            style={{ boxShadow: 'rgba(96, 165, 250, 0.8) 0px 0px 6px' }}
          />
          AI-Powered Growth Agency
        </div>

        {/* Headline */}
        <h1
          className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-white leading-[1.08] mb-6"
          style={{ letterSpacing: '-0.02em' }}
        >
          Turn Attention Into{' '}
          <br className="hidden sm:block" />
          <span className="gradient-text-animated">Revenue with AI</span>
        </h1>

        {/* Subheadline */}
        <p className="text-base sm:text-lg lg:text-xl text-neutral-400 max-w-2xl mx-auto mb-10 leading-relaxed">
          We help brands scale content, automate workflows,
          <br className="hidden sm:block" />
          and convert views into clients — with AI.
        </p>

        {/* CTA Group */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://calendly.com/creatormaska/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="glow-button inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold text-white"
          >
            Book a Call
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </a>
          <button
            onClick={() => document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' })}
            className="outline-button inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400" aria-hidden="true">
              <polygon points="6 3 20 12 6 21 6 3" />
            </svg>
            View Work
          </button>
        </div>

        {/* Trust Line */}
        <p className="mt-8 text-xs text-neutral-600 tracking-[0.15em] uppercase font-medium">
          Elevate with AI &nbsp;·&nbsp; Trusted by 100+ brands worldwide
        </p>
      </div>

      {/* Bottom Fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-40 pointer-events-none"
        style={{ background: 'linear-gradient(to top, rgb(5, 5, 5), transparent)' }}
      />

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 opacity-40 animate-bounce">
        <div className="w-px h-10 bg-gradient-to-b from-transparent via-blue-500 to-transparent" />
      </div>
    </section>
  );
}
